import type { Translations } from '../types.js';

export const en: Translations = {
  nav: {
    overview: 'Overview',
    users: 'Users',
    devices: 'Devices',
    subscriptions: 'Subscriptions',
    entitlements: 'Entitlements',
    features: 'Feature Flags',
    config: 'Remote Config',
    commands: 'Commands',
    snapshots: 'Snapshots',
    audit: 'Audit Logs',
    system: 'System Health',
    consoleTag: 'Console v1.0',
    signOut: 'Sign out',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
  },
  topbar: {
    admin: 'Admin',
    searchPlaceholder: 'Search users, devices, commands...',
    searchShortcut: '⌘K',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    systemTheme: 'System Mode',
    notifications: 'Notifications',
    profile: 'Profile',
    language: 'Language',
  },
  login: {
    title: 'Hisob Admin Console',
    subtitle: 'Privacy-first administration & fleet telemetry',
    emailLabel: 'Admin Email',
    emailPlaceholder: 'e.g. admin@hisob.uz',
    secretLabel: 'Access Secret',
    secretPlaceholder: '••••••••••••••••',
    submitButton: 'Sign in to Console',
    signingIn: 'Signing in...',
    errorRequired: 'Please enter admin email and secret key',
    errorInvalid: 'Invalid admin credentials',
    devSwitcherTitle: 'Quick Role Switcher (Dev Mode)',
  },
  common: {
    search: 'Search',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    restore: 'Restore',
    filter: 'Filter',
    refresh: 'Refresh',
    confirm: 'Confirm',
    close: 'Close',
    loading: 'Loading...',
    actions: 'Actions',
    status: 'Status',
    success: 'Success',
    error: 'Error',
    view: 'View',
    edit: 'Edit',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    failed: 'Failed',
    revoked: 'Revoked',
    copied: 'Copied',
    copy: 'Copy',
    back: 'Back',
    authenticating: 'Authenticating admin session...',
    yes: 'Yes',
    no: 'No',
    resetFilters: 'Reset filters',
    firstPage: 'First Page',
    nextPage: 'Next Page',
    details: 'Details',
    viewAll: 'View all',
  },
  statusPages: {
    notFoundTitle: '404 — Page Not Found',
    notFoundDesc: 'The console route or administrative resource you requested does not exist or has been relocated.',
    returnToDashboard: 'Return to Dashboard',
    forbiddenTitle: '403 — Action Forbidden',
    forbiddenDesc: 'Your current admin role does not possess sufficient RBAC permissions to view or mutate this resource.',
    returnToSafety: 'Return to Safety',
  },
  searchModal: {
    title: 'Global Operational Search',
    placeholder: 'Search by User ID, Device ID, Command ID, App Version...',
    noResults: 'No operational records found matching: ',
    typeMinChars: 'Type at least 2 characters to search across users, devices, commands and more.',
    pressEsc: 'Press ESC to close',
    tip: 'Tip: You can navigate directly to matching records',
    users: 'Users',
    devices: 'Devices',
    commands: 'Commands',
    snapshots: 'Support Snapshots',
    subscriptions: 'Subscriptions',
  },
  dashboard: {
    title: 'Dashboard Overview',
    totalUsers: 'Total Users',
    activeDevices: 'Active Devices',
    onlineDevices: 'Online Devices',
    premiumUsers: 'Premium Users',
    subscriptions: 'Subscriptions',
    pendingCommands: 'Pending Commands',
    activeSnapshots: 'Active Snapshots',
    featureFlagsOn: 'Feature Flags On',
    systemHealth: 'System Health',
    recentActivity: 'Recent Activity',
    failedToLoad: 'Failed to load dashboard metrics',
    bannerTitle: 'System Operational Status',
    bannerDesc: 'Cloudflare edge is routing traffic normally. Fleet telemetry reporting live.',
    dispatchCommand: 'Dispatch Command',
    inspectFleet: 'Inspect Fleet',
    infraHealthTitle: 'Cloudflare Edge Infrastructure Health',
    checkedAgo: 'Checked {time}',
    chartActiveDevices: 'Daily Active Devices (7 Days)',
    chartPlatforms: 'Platform Distribution',
    recentFleetCommands: 'Recent Fleet Commands',
    recentAuditEvents: 'Recent Admin Audit Events',
    deviceLabel: 'Device',
    targetLabel: 'Target',
  },
  users: {
    title: 'User Management',
    description: 'User accounts, Telegram authentication link status, and last activity monitoring',
    searchPlaceholder: 'User ID, Phone number or Telegram Chat ID...',
    allTelegram: 'All Telegram Statuses',
    linkedOnly: 'Linked Only',
    unlinkedOnly: 'Unlinked Only',
    allStatuses: 'All Statuses',
    activeStatus: 'Active',
    suspendedStatus: 'Suspended',
    deletedStatus: 'Deleted',
    tableUserId: 'User ID',
    tablePhone: 'Phone Number',
    tableTelegram: 'Telegram Status',
    tableLastLogin: 'Last Login / Verified',
    tableStatus: 'Status',
    tableRegistered: 'Registered',
    tableActions: 'Actions',
    noPhone: 'Not available',
    noLoginRecorded: 'No login recorded',
    emptyTitle: 'No users found',
    emptyDesc: 'There are no user accounts matching your search or filter criteria.',
    totalCount: 'Total: {count} users',
    loadError: 'Failed to load users',
  },
  userDetail: {
    backButton: 'Back to Users',
    title: 'User Account Details',
    privacyNoticeTitle: 'Privacy-First Architecture Notice:',
    privacyNoticeDesc: 'Per Hisob core security architecture, financial data (balances, transactions, banks, cards) are never stored on the server and are strictly maintained locally on user devices. This administrative profile manages device keys, entitlements, and diagnostic requests only.',
    accountOverview: 'Account Overview',
    phoneNumber: 'Phone Number',
    telegramChatId: 'Telegram Chat ID',
    registeredAt: 'Registration Date',
    lastActive: 'Last Active',
    accountStatus: 'Account Status',
    registeredDevices: 'Registered Devices',
    noDevices: 'No devices registered under this account.',
    entitlementsTitle: 'User Entitlements',
    noEntitlements: 'No active subscriptions or special privileges.',
    notFoundTitle: 'User not found',
  },
  devices: {
    title: 'Mobile Devices Fleet',
    description: 'Registered iOS & Android client instances with security token status and fleet telemetry',
    searchPlaceholder: 'Search by Device ID, User ID, Model...',
    allPlatforms: 'All Platforms',
    tableDeviceId: 'Device ID',
    tableUserId: 'User ID',
    tableModelPlatform: 'Model & Platform',
    tableAppVersion: 'App Version',
    tableStatus: 'Status',
    tableLastSeen: 'Last Seen',
    tableActions: 'Actions',
    online: 'Online',
    offline: 'Offline',
    revoked: 'Revoked',
    revokeButton: 'Revoke',
    revokeModalTitle: 'Revoke Device Access',
    revokeModalMessage: 'Are you sure you want to revoke device "{id}"? All active JWT tokens will immediately be rejected, and the device will be disconnected from the Hisob backend.',
    revokeConfirmText: 'Permanently Revoke',
    revokeSuccessToast: 'Device revoked successfully',
    emptyTitle: 'No devices found',
    emptyDesc: 'There are no devices matching your criteria.',
    totalCount: 'Showing {count} devices',
    loadError: 'Failed to load devices',
  },
  deviceDetail: {
    backButton: 'Back to Devices',
    title: 'Device Details',
    hardwareSpecs: 'Hardware & OS Specifications',
    model: 'Model',
    platform: 'Platform',
    osVersion: 'OS Version',
    appVersion: 'App Version',
    registeredAt: 'Registered At',
    lastSeen: 'Last Contact',
    securityStatus: 'Security & Token Status',
    deviceTokenStatus: 'Device Token',
    userId: 'Bound User ID',
    quickCommands: 'Quick Fleet Commands',
    dispatchCommandBtn: 'Dispatch Command',
    commandsHistory: 'Command Execution History',
    noCommands: 'No commands queued or executed for this device.',
    modalTitle: 'Queue Command for Device',
    commandTypeLabel: 'Command Type',
    commandReasonLabel: 'Reason / Purpose',
    commandReasonPlaceholder: 'e.g., investigating user report',
    commandTtlLabel: 'Expiration (Hours)',
    submitCommand: 'Queue Command',
    revokeConfirmTitle: 'Revoke Device Access',
  },
  subscriptions: {
    title: 'Store Subscriptions Registry',
    description: 'Normalized App Store, Google Play & RevenueCat purchase receipts validated at edge',
    searchPlaceholder: 'Search by User, External ID, Product...',
    tableId: 'ID',
    tableUserId: 'User ID',
    tableStore: 'Store',
    tableProduct: 'Product ID',
    tableStatus: 'Status',
    tableExpiresAt: 'Expires At',
    tableCreatedAt: 'Created At',
    emptyTitle: 'No subscriptions found',
    emptyDesc: 'No subscriptions matching your criteria were found.',
    loadError: 'Failed to load subscriptions',
  },
  entitlements: {
    title: 'Verified User Entitlements',
    description: 'Authoritative feature privileges computed from validated receipts or administrative grants',
    searchPlaceholder: 'Search by User ID or Entitlement Key...',
    tableUserId: 'User ID',
    tableEntitlement: 'Entitlement Key',
    tableStatus: 'Status',
    tableGrantedBy: 'Granted By',
    tableExpiresAt: 'Expires At',
    emptyTitle: 'No entitlements found',
    emptyDesc: 'No user entitlements have been granted yet.',
    loadError: 'Failed to load entitlements',
  },
  features: {
    title: 'Feature Flags Management',
    description: 'Safely enable, test, and gradually roll out mobile app capabilities without app store releases',
    searchPlaceholder: 'Search by flag key...',
    newFlagBtn: 'New Feature Flag',
    tableKey: 'Flag Key',
    tableStatus: 'Status',
    tableRollout: 'Rollout %',
    tableMinVersion: 'Min Version',
    tablePlatform: 'Platform',
    tablePremiumOnly: 'Premium Only',
    tableActions: 'Actions',
    freeTier: 'Free Tier',
    editModalTitle: 'Edit Flag: {key}',
    createModalTitle: 'Create Feature Flag',
    flagKeyLabel: 'Flag Key (e.g. telegram_otp_auth, new_dashboard)',
    flagKeyPlaceholder: 'e.g. premium.ai_insights',
    enabledLabel: 'Enabled',
    enabledHelp: 'Instantly toggles this feature across matching fleet segments.',
    rolloutLabel: 'Rollout Percentage (%)',
    rolloutHelp: 'Backend applies consistent SHA-256 hash modulo against Device ID. No random flicker across sessions.',
    platformLabel: 'Target Platform',
    platformAll: 'All Platforms',
    platformIos: 'iOS Only',
    platformAndroid: 'Android Only',
    platformWeb: 'Web Only',
    minVersionLabel: 'Minimum App Version',
    minVersionPlaceholder: 'e.g. 1.2.0 (blank for any)',
    premiumOnlyLabel: 'Premium subscribers only',
    premiumOnlyHelp: 'Only devices with active subscription or manual promo will receive flag = true.',
    configJsonLabel: 'Additional JSON Payload',
    jsonMalformedError: 'Config JSON is malformed',
    saveBtn: 'Save Changes',
    createBtn: 'Create Flag',
    saveSuccess: 'Feature flag updated successfully',
    createSuccess: 'Feature flag created successfully',
    emptyTitle: 'No feature flags found',
    emptyDesc: 'There are no feature flags configured in the system.',
    loadError: 'Failed to load feature flags',
  },
  config: {
    title: 'Remote App Configuration',
    description: 'Global mobile app parameters, forced version upgrades, and emergency maintenance mode',
    minVersionTitle: 'Minimum Supported App Version',
    minVersionDesc: 'Clients running a version below this will be forced to upgrade immediately.',
    latestVersionTitle: 'Current Latest Version',
    latestVersionDesc: 'The latest official release version available on app stores.',
    forceUpdateTitle: 'Force Version Upgrade',
    forceUpdateDesc: 'When enabled, users on older app versions cannot proceed until updated.',
    maintenanceTitle: 'Emergency Maintenance Mode',
    maintenanceDesc: 'Places the entire mobile API into a temporary maintenance state.',
    maintenanceMessageTitle: 'User Maintenance Message',
    storeUrlsTitle: 'App Store Download Links',
    androidUrl: 'Google Play Store URL',
    iosUrl: 'Apple App Store URL',
    changelogTitle: 'Release Changelog',
    saveConfigBtn: 'Save & Publish to Edge',
    confirmModalTitle: 'Publish Remote Configuration',
    confirmModalMessage: 'Are you sure you want to update global config? Changes will instantly propagate to all mobile devices worldwide.',
    saveSuccess: 'App configuration updated and pushed to Cloudflare Edge.',
    loadError: 'Failed to load app configuration',
  },
  commands: {
    title: 'Device Commands Queue',
    description: 'Fleet instructions dispatched to mobile clients for cache clearing, diagnostics, and session controls',
    searchPlaceholder: 'Search by Command ID, Device ID, Type...',
    allStatuses: 'All Statuses',
    tableId: 'Command ID',
    tableDevice: 'Device ID',
    tableType: 'Type',
    tableStatus: 'Status',
    tableCreatedBy: 'Created By',
    tableCreatedAt: 'Created At',
    tableExpiresAt: 'Expires At',
    tableActions: 'Actions',
    cancelCommandBtn: 'Cancel',
    inspectModalTitle: 'Command Inspection',
    cancelConfirmTitle: 'Cancel Command',
    cancelConfirmMessage: 'Are you sure you want to cancel this command? It will not be executed on the client.',
    cancelSuccess: 'Command cancelled successfully',
    emptyTitle: 'No commands found',
    emptyDesc: 'There are no fleet commands matching your search.',
    loadError: 'Failed to load commands',
  },
  snapshots: {
    title: 'Support & Diagnostic Snapshots',
    description: 'User-consented, encrypted temporary device diagnostic archives stored in cold storage',
    tableId: 'Snapshot ID',
    tableUserId: 'User ID',
    tableDeviceId: 'Device ID',
    tableExpiresIn: 'Time Remaining',
    tableCreatedAt: 'Captured At',
    tableActions: 'Actions',
    inspectBtn: 'Inspect & Decrypt',
    deleteBtn: 'Delete',
    inspectModalTitle: 'Decrypt Diagnostic Snapshot',
    inspectPrompt: 'Please specify the audit reason for decrypting and inspecting this customer diagnostic archive:',
    reasonPlaceholder: 'e.g., investigating customer issue #4982',
    submitInspect: 'Decrypt & Inspect',
    deleteModalTitle: 'Destroy Support Snapshot',
    deleteModalMessage: 'Are you sure you want to permanently delete this snapshot from R2 storage immediately?',
    deleteSuccess: 'Diagnostic snapshot permanently destroyed.',
    emptyTitle: 'No diagnostic snapshots',
    emptyDesc: 'There are currently no active diagnostic snapshots available.',
    loadError: 'Failed to load snapshots',
  },
  audit: {
    title: 'Security & Audit Trail',
    description: 'Immutable, tamper-evident log of all administrative actions and authentication events',
    tabAuth: 'Authentication & OTP Events',
    tabAll: 'All Administrative Actions',
    searchPlaceholder: 'Search by phone number, chat ID, admin ID, action...',
    allActions: 'All Actions',
    tableTimestamp: 'Timestamp',
    tableActor: 'Actor',
    tableAction: 'Action',
    tableTarget: 'Target',
    tableDetails: 'Details',
    emptyTitle: 'No audit logs found',
    emptyDesc: 'There are no audit events matching your criteria.',
    inspectModalTitle: 'Audit Event Details',
    loadError: 'Failed to load audit logs',
  },
  system: {
    title: 'System & Edge Infrastructure Health',
    description: 'Cloudflare global edge runtime, D1 relational cluster, KV, R2, and queues operational status',
    refreshBtn: 'Refresh Health',
    allSystemsOperational: 'All Systems Fully Operational',
    apiWorker: 'Cloudflare Workers Edge API',
    apiWorkerDesc: 'Global V8 serverless runtime serving /api/v1/* endpoints',
    d1Db: 'Cloudflare D1 Relational DB',
    d1DbDesc: 'SQLite edge cluster with automated multi-region replicas',
    kvConfig: 'Cloudflare KV Namespaces',
    kvConfigDesc: 'Sub-millisecond global key-value store for config and flags',
    r2Storage: 'Cloudflare R2 Object Storage',
    r2StorageDesc: 'S3-compatible zero-egress encrypted cold snapshot store',
    queues: 'Cloudflare Queues',
    queuesDesc: 'Asynchronous streaming for device telemetry and audit events',
    cronWorker: 'Automated Cron Trigger',
    cronWorkerDesc: 'Hourly scheduled task purging expired diagnostic snapshots',
    latency: 'Latency: {val}',
    loadError: 'Failed to probe system health',
  },
  commandTypes: {
    export_snapshot: 'Export Diagnostic Snapshot',
    clear_cache: 'Clear Local Cache',
    refresh_config: 'Refresh App Config',
    refresh_entitlements: 'Refresh Entitlements',
    logout: 'Graceful Logout',
    invalidate_session: 'Invalidate Session',
    diagnostics: 'Run Self-Diagnostics',
  },
};
